/**
 * Starter templates for the new htmljs document engine.
 *
 * When an admin creates a document with engine "htmljs", the studio offers these so they begin from
 * a working example instead of a blank editor. Authoring is declarative Handlebars by default
 * (mode: 'handlebars'). Fields are bound by their DISPLAY NAME — wrap any name containing a space or
 * punctuation in square brackets: {{entry.Customer}}, {{entry.[Invoice Date]}}; loop a table field
 * with {{#each entry.[Line Items]}}. SYSTEM FIELDS are always available on every entry:
 * {{entry.state}} (workflow status), {{entry.date}}, {{entry.createdBy}}/{{entry.createdDate}},
 * {{entry.updatedBy}}/{{entry.updatedDate}}, {{entry.documentId}}. Format with the runtime helpers
 * ({{currency x "MYR"}}, {{date x "short"}}, {{qr x}}). The imperative escape hatch (mode: 'js' +
 * inistate.onRender) is for the rare procedural template.
 *
 * NOTE: the field names in these starters are PLACEHOLDERS — replace them with your module's actual
 * field display names (system fields state/date/createdBy/… are real and always present).
 *
 * Each starter mirrors the /doc/bundle shape: { bundle: { mode, html, css, js }, page }.
 */

export const DEFAULT_HTMLJS_MODE = 'handlebars';

/** A generic, ready-to-adapt invoice — header + line-item table + totals + optional e-invoice QR. */
const invoiceStarter = {
  id: 'invoice',
  name: 'Invoice',
  description: 'Header, bill-to, line items, totals and an optional e-invoice QR. Declarative Handlebars.',
  bundle: {
    mode: 'handlebars',
    html: `{{> letterhead}}

<section class="info">
  <div><span>Bill To</span><div class="cust">{{entry.Customer}}</div><pre>{{entry.[Bill To]}}</pre></div>
  <div class="meta">
    <div><span>Date</span><b>{{date entry.[Invoice Date] "short"}}</b></div>
    <div><span>Terms</span><b>{{default entry.[Payment Term] "30 Days"}}</b></div>
    <div><span>Status</span><b>{{entry.state}}</b></div>
  </div>
</section>

<table class="items">
  <thead><tr>
    <th>No</th><th class="desc">Description</th><th>Qty</th>
    <th class="n">Unit Price</th><th class="n">Amount</th>
  </tr></thead>
  <tbody>
    {{#each entry.[Line Items]}}
    <tr>
      <td>{{No}}</td>
      <td class="desc">{{Description}}</td>
      <td>{{Quantity}}</td>
      <td class="n">{{currency [Unit Price] "MYR"}}</td>
      <td class="n">{{currency Amount "MYR"}}</td>
    </tr>
    {{/each}}
  </tbody>
</table>

<table class="totals">
  <tr><td class="lbl">Subtotal</td><td class="amt">{{currency entry.Subtotal "MYR"}}</td></tr>
  <tr><td class="lbl">Tax</td><td class="amt">{{currency entry.Tax "MYR"}}</td></tr>
  <tr class="total"><td class="lbl">Total</td><td class="amt">{{currency entry.[Grand Total] "MYR"}}</td></tr>
</table>

{{#if entry.[E-Invoice Validation Link]}}
<div class="einv">{{qr entry.[E-Invoice Validation Link]}}<div class="uid">{{entry.[E-Invoice Unique Id]}}</div></div>
{{/if}}

<div class="remarks"><div class="lbl">Remarks:</div><div>{{default (nl2br entry.Remarks) ""}}</div></div>

<div class="audit">Created by {{default entry.createdBy "—"}} on {{date entry.createdDate "short"}} · Last updated {{date entry.updatedDate "short"}}</div>

{{> footer}}`,
    css: `body{font:12px/1.45 Arial,Helvetica,sans-serif;color:#222}
.lh{display:flex;align-items:flex-start;justify-content:space-between;border-bottom:3px solid #2095c0;padding-bottom:8px}
.lh .co-name{color:#1A5581;font-size:20px;font-weight:700}
.lh .co-line{font-size:10px;color:#808080}
.lh .t{font-size:30px;color:#2095c0;text-align:right}
.lh .docno{font-size:14px;color:#808080;font-weight:700;text-align:right;margin-top:8px}
.info{display:flex;justify-content:space-between;margin:14px 0;font-size:12px}
.info span{display:block;color:#888;font-size:11px}
.info .cust{font-weight:600}
.info pre{font:inherit;margin:0;white-space:pre-wrap}
.info .meta div{display:flex;gap:10px;justify-content:flex-end}
table.items{width:100%;border-collapse:collapse;font-size:12px;border:1px solid #ccc;margin-top:4px}
table.items th,table.items td{border:1px solid #ccc;padding:4px 6px;text-align:center}
table.items th{background:#f0f0f0;font-weight:700}
table.items td.desc,table.items th.desc{text-align:left;width:55%}
table.items td.n,table.items th.n{text-align:right}
table.totals{margin-top:14px;margin-left:auto;border-collapse:collapse;font-size:12px}
table.totals td{padding:4px 10px}
table.totals td.lbl{font-weight:700;padding-right:40px}
table.totals td.amt{text-align:right;font-weight:700;min-width:110px}
table.totals tr.total td{font-size:16px;border-top:2px solid #333}
.einv{text-align:right;margin-top:8px}
.einv .inistate-qr{height:64px;image-rendering:pixelated}
.einv .uid{font-size:9px;color:#555}
.remarks{margin-top:10px;font-size:12px}
.remarks div{white-space:pre-wrap}
.audit{margin-top:12px;font-size:10px;color:#888;border-top:1px solid #eee;padding-top:6px}`,
    // Register shared partials here (workspace-level components). Customize for your company.
    js: `Handlebars.registerPartial('letterhead', \`
  <header class="lh">
    <div>
      <div class="co-name">Your Company Sdn Bhd</div>
      <div class="co-line">123 Example Street, City, Country</div>
      <div class="co-line">T: 00-000 0000  E: hello@example.com</div>
    </div>
    <div><div class="t">INVOICE</div><div class="docno">{{entry.documentId}}</div></div>
  </header>\`);

Handlebars.registerPartial('footer', \`
  <div style="margin-top:14px;font-size:10px;color:#808080">
    <div style="text-decoration:underline">Payment Details:</div>
    <div>Make all cheques payable to Your Company Sdn Bhd</div>
  </div>\`);`,
  },
  page: {
    size: 'A4',
    orientation: 'portrait',
    margin: '16mm',
    footer:
      '<div style="font-size:9px;width:100%;text-align:center;color:#999">Page <span class="pageNumber"></span> of <span class="totalPages"></span></div>',
  },
};

/** Minimal blank starter — just enough scaffolding to start binding fields. */
const blankStarter = {
  id: 'blank',
  name: 'Blank',
  description: 'An empty Handlebars document. Bind with {{entry.[Display Name]}} and {{#each entry.[Table]}}.',
  bundle: {
    mode: 'handlebars',
    html: `<article>
  <h1>{{entry.documentId}}</h1>
  <p class="sub">Status: {{entry.state}} · {{date entry.createdDate "short"}}</p>
  <!-- Bind fields by DISPLAY NAME, bracketing names with spaces: {{entry.Customer}}, {{entry.[Grand Total]}}.
       Loop a table field: {{#each entry.[Line Items]}} … {{/each}}. System fields (always present):
       {{entry.state}}, {{entry.date}}, {{entry.createdBy}}, {{entry.createdDate}}, {{entry.updatedBy}},
       {{entry.updatedDate}}. Format: {{currency x "MYR"}} / {{date x "short"}}; QR via {{qr x}}. -->
</article>`,
    css: `body{font:13px/1.5 Inter,Arial,sans-serif;color:#222;padding:24px}`,
    js: ``,
  },
  page: { size: 'A4', orientation: 'portrait', margin: '18mm' },
};

/** A list/summary report over MANY entries (dataSource: Entries) — status column, totals, grouping-ready.
 *  Toggle Data = "Report (many)" in the modal so /doc/data serves entries[]. */
const reportStarter = {
  id: 'report',
  name: 'Report',
  description: 'A list over many entries (dataSource: Entries): status column, dates and a total row.',
  dataSource: 'Entries',
  bundle: {
    mode: 'handlebars',
    html: `<header class="rpt-hd">
  <div class="rpt-title">{{module.name}} Report</div>
  <div class="rpt-sub">Generated {{date context.now "medium"}}{{#if entriesInfo.truncated}} · showing first {{entries.length}} of {{entriesInfo.total}}{{/if}}</div>
</header>

<table class="rpt">
  <thead><tr>
    <th>Document</th><th>Customer</th><th>Status</th><th>Created</th><th class="n">Grand Total</th>
  </tr></thead>
  <tbody>
    {{#each entries}}
    <tr>
      <td>{{this.documentId}}</td>
      <td>{{default this.Customer "—"}}</td>
      <td><span class="badge">{{this.state}}</span></td>
      <td>{{date this.createdDate "short"}}</td>
      <td class="n">{{currency this.[Grand Total] "MYR"}}</td>
    </tr>
    {{/each}}
  </tbody>
  <tfoot>
    <tr class="total"><td colspan="4">Total ({{entries.length}})</td><td class="n">{{currency (sum entries "Grand Total") "MYR"}}</td></tr>
  </tfoot>
</table>

<!-- Group with subtotals instead of a flat list:
  {{#group entries by="Status" sum="Grand Total"}}
    <h3>{{key}} — {{currency total "MYR"}}</h3>
    {{#each items}} … {{this.documentId}} … {{/each}}
  {{/group}} -->`,
    css: `body{font:12px/1.45 Arial,Helvetica,sans-serif;color:#222}
.rpt-hd{border-bottom:2px solid #2095c0;padding-bottom:6px;margin-bottom:10px}
.rpt-hd .rpt-title{font-size:18px;font-weight:700;color:#1A5581}
.rpt-hd .rpt-sub{font-size:10px;color:#888}
table.rpt{width:100%;border-collapse:collapse;font-size:11px}
table.rpt th,table.rpt td{border:1px solid #ddd;padding:4px 6px;text-align:left}
table.rpt th{background:#f0f0f0;font-weight:700}
table.rpt td.n,table.rpt th.n{text-align:right}
table.rpt .badge{display:inline-block;padding:1px 8px;border-radius:10px;background:#eef3f7;color:#1A5581;font-size:10px}
table.rpt tfoot .total td{font-weight:700;border-top:2px solid #333;background:#fafafa}`,
    js: ``,
  },
  page: {
    size: 'A4',
    orientation: 'portrait',
    margin: '14mm',
    footer:
      '<div style="font-size:9px;width:100%;text-align:center;color:#999">Page <span class="pageNumber"></span> of <span class="totalPages"></span></div>',
  },
};

export const htmljsStarters = [invoiceStarter, reportStarter, blankStarter];

/** The starter a new htmljs document opens with by default. */
export function defaultHtmljsStarter() {
  return JSON.parse(JSON.stringify(invoiceStarter));
}
